'use client';

import React, { useState } from 'react';
import { Plus, Search, Eye, CheckCircle, XCircle, Clock, X, FileText, Scissors, Filter, ChevronDown, User, Calendar, TrendingDown, Shield, Activity, Layers,  } from 'lucide-react';
import { FARMS, HOUSES_BY_FARM, MOCK_FLOCK_SUMMARIES } from '../../components/birdRecordsData';

// ─── Types ────────────────────────────────────────────────────────────────────

type CullingStatus = 'draft' | 'pending_approval' | 'approved' | 'posted' | 'rejected' | 'cancelled';

type CullingReason =
  | 'disease' |'injury' |'performance' |'deformity' |'age' |'production_decision' |'other';

interface CullingRecord {
  id: string;
  cullingNumber: string;
  cullingDate: string;
  farm: string;
  house: string;
  flock: string;
  flockNumber: string;
  birdType: string;
  breed: string;
  quantity: number;
  reason: CullingReason;
  reasonDetail?: string;
  populationBefore?: number;
  populationAfter?: number;
  authorizedBy: string;
  recordedBy: string;
  approvedBy?: string;
  rejectedBy?: string;
  rejectionReason?: string;
  notes?: string;
  status: CullingStatus;
  submittedAt?: string;
  approvedAt?: string;
  postedAt?: string;
  createdAt: string;
}

// ─── Mock Data ────────────────────────────────────────────────────────────────

const MOCK_CULLING_RECORDS: CullingRecord[] = [
  {
    id: 'cull-001',
    cullingNumber: 'CUL-2026-000001',
    cullingDate: '2026-08-05',
    farm: 'Idudi Poultry Farm',
    house: 'House A1',
    flock: 'Batch A1 — Cobb 500',
    flockNumber: 'BR-2026-001',
    birdType: 'Broiler',
    breed: 'Cobb 500',
    quantity: 12,
    reason: 'disease',
    reasonDetail: 'Newcastle disease — confirmed by vet',
    populationBefore: 2000,
    populationAfter: 1988,
    authorizedBy: 'Dr. Sarah Namukasa',
    recordedBy: 'James Okello',
    approvedBy: 'Farm Manager',
    notes: 'Vet-confirmed diagnosis. Affected birds isolated and culled to prevent spread.',
    status: 'posted',
    submittedAt: '2026-08-05T10:00:00Z',
    approvedAt: '2026-08-05T11:30:00Z',
    postedAt: '2026-08-05T11:35:00Z',
    createdAt: '2026-08-05T09:00:00Z',
  },
  {
    id: 'cull-002',
    cullingNumber: 'CUL-2026-000002',
    cullingDate: '2026-08-12',
    farm: 'Idudi Poultry Farm',
    house: 'Layer House 01',
    flock: 'Layers Batch L1 — Lohmann',
    flockNumber: 'LY-2026-001',
    birdType: 'Layer',
    breed: 'Lohmann Brown',
    quantity: 8,
    reason: 'injury',
    reasonDetail: 'Leg injuries — unable to access feeders',
    populationBefore: 7800,
    populationAfter: 7792,
    authorizedBy: 'Farm Manager',
    recordedBy: 'Moses Wafula',
    approvedBy: 'Operations Director',
    status: 'posted',
    submittedAt: '2026-08-12T08:00:00Z',
    approvedAt: '2026-08-12T09:00:00Z',
    postedAt: '2026-08-12T09:05:00Z',
    createdAt: '2026-08-12T07:30:00Z',
  },
  {
    id: 'cull-003',
    cullingNumber: 'CUL-2026-000003',
    cullingDate: '2026-08-20',
    farm: 'Idudi Poultry Farm',
    house: 'House B1',
    flock: 'Batch B1 — Ross 308',
    flockNumber: 'BR-2026-003',
    birdType: 'Broiler',
    breed: 'Ross 308',
    quantity: 25,
    reason: 'performance',
    reasonDetail: 'Runts — below 60% of average flock weight at day 28',
    populationBefore: 3200,
    populationAfter: 3175,
    authorizedBy: 'Farm Manager',
    recordedBy: 'James Okello',
    status: 'pending_approval',
    submittedAt: '2026-08-20T14:00:00Z',
    createdAt: '2026-08-20T13:30:00Z',
  },
  {
    id: 'cull-004',
    cullingNumber: 'CUL-2026-000004',
    cullingDate: '2026-09-01',
    farm: 'Kampala Demo Farm',
    house: 'Layer House 03',
    flock: 'Layers Batch L3 — ISA Brown',
    flockNumber: 'LY-2026-003',
    birdType: 'Layer',
    breed: 'ISA Brown',
    quantity: 5,
    reason: 'deformity',
    reasonDetail: 'Crossed beak deformity — unable to feed independently',
    populationBefore: 4500,
    populationAfter: 4495,
    authorizedBy: 'Dr. Sarah Namukasa',
    recordedBy: 'Peter Ssemakula',
    status: 'approved',
    submittedAt: '2026-09-01T10:00:00Z',
    approvedAt: '2026-09-01T11:00:00Z',
    createdAt: '2026-09-01T09:30:00Z',
  },
  {
    id: 'cull-005',
    cullingNumber: 'CUL-2026-000005',
    cullingDate: '2026-09-04',
    farm: 'Idudi Poultry Farm',
    house: 'Breeder House 01',
    flock: 'Breeders Batch BRD-001',
    flockNumber: 'BRD-2026-001',
    birdType: 'Breeder',
    breed: 'Ross Breeder',
    quantity: 18,
    reason: 'age',
    reasonDetail: 'End of production cycle — 72 weeks, production below 30%',
    populationBefore: 1200,
    populationAfter: 1182,
    authorizedBy: 'Operations Director',
    recordedBy: 'Sarah Namukasa',
    status: 'draft',
    createdAt: '2026-09-04T08:00:00Z',
  },
  {
    id: 'cull-006',
    cullingNumber: 'CUL-2026-000006',
    cullingDate: '2026-09-05',
    farm: 'Idudi Poultry Farm',
    house: 'House A2',
    flock: 'Batch A2 — Cobb 500',
    flockNumber: 'BR-2026-002',
    birdType: 'Broiler',
    breed: 'Cobb 500',
    quantity: 3,
    reason: 'injury',
    reasonDetail: 'Trampling injuries — found during morning check',
    populationBefore: 2850,
    populationAfter: 2847,
    authorizedBy: 'Farm Manager',
    recordedBy: 'James Okello',
    status: 'rejected',
    rejectedBy: 'Operations Director',
    rejectionReason: 'Insufficient documentation — vet report required for injury culling above 2 birds',
    submittedAt: '2026-09-05T07:00:00Z',
    createdAt: '2026-09-05T06:30:00Z',
  },
];

// ─── Config ───────────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<CullingStatus, { label: string; color: string; bg: string; icon: React.ElementType }> = {
  draft: { label: 'Draft', color: '#64748b', bg: '#f1f5f9', icon: FileText },
  pending_approval: { label: 'Pending Approval', color: '#d97706', bg: '#fef3c7', icon: Clock },
  approved: { label: 'Approved', color: '#2563eb', bg: '#dbeafe', icon: CheckCircle },
  posted: { label: 'Posted', color: '#16a34a', bg: '#dcfce7', icon: CheckCircle },
  rejected: { label: 'Rejected', color: '#dc2626', bg: '#fee2e2', icon: XCircle },
  cancelled: { label: 'Cancelled', color: '#6b7280', bg: '#f3f4f6', icon: X },
};

const REASON_CONFIG: Record<CullingReason, { label: string; color: string; bg: string; description: string }> = {
  disease: { label: 'Disease', color: '#dc2626', bg: '#fee2e2', description: 'Confirmed or suspected infectious disease' },
  injury: { label: 'Injury', color: '#ea580c', bg: '#ffedd5', description: 'Physical injury preventing normal function' },
  performance: { label: 'Poor Performance', color: '#d97706', bg: '#fef3c7', description: 'Below-standard growth or production metrics' },
  deformity: { label: 'Deformity', color: '#7c3aed', bg: '#ede9fe', description: 'Physical deformity affecting welfare or productivity' },
  age: { label: 'Age / End of Cycle', color: '#0891b2', bg: '#cffafe', description: 'End of productive life cycle' },
  production_decision: { label: 'Production Decision', color: '#2563eb', bg: '#dbeafe', description: 'Management decision for production optimization' },
  other: { label: 'Other', color: '#64748b', bg: '#f1f5f9', description: 'Other authorized reason' },
};

const APPROVERS = [
  'Farm Manager',
  'Operations Director',
  'Dr. Sarah Namukasa (Vet)',
  'General Manager',
];

const RECORDERS = [
  'James Okello',
  'Moses Wafula',
  'Sarah Namukasa',
  'Peter Ssemakula',
  'Grace Atim',
];

// ─── KPI Summary ──────────────────────────────────────────────────────────────

function CullingSummaryCards({ records }: { records: CullingRecord[] }) {
  const posted = records.filter(r => r.status === 'posted');
  const pending = records.filter(r => r.status === 'pending_approval');
  const totalCulled = posted.reduce((s, r) => s + r.quantity, 0);
  const thisMonth = posted.filter(r => r.cullingDate.startsWith('2026-09'));
  const thisMonthQty = thisMonth.reduce((s, r) => s + r.quantity, 0);

  const cards = [
    { label: 'Total Culled (Posted)', value: totalCulled.toLocaleString(), icon: Scissors, color: '#dc2626', bg: '#fee2e2' },
    { label: 'This Month', value: thisMonthQty.toLocaleString(), icon: Calendar, color: '#d97706', bg: '#fef3c7' },
    { label: 'Pending Approval', value: pending.length.toString(), icon: Clock, color: '#2563eb', bg: '#dbeafe' },
    { label: 'Total Records', value: records.length.toString(), icon: FileText, color: '#7c3aed', bg: '#ede9fe' },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
      {cards.map(c => (
        <div key={c.label} className="bg-white rounded-xl border border-border/60 shadow-sm p-4 flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg flex-shrink-0" style={{ backgroundColor: c.bg }}>
            <c.icon className="h-4 w-4" style={{ color: c.color }} />
          </div>
          <div className="min-w-0">
            <p className="text-xs text-muted-foreground font-medium truncate">{c.label}</p>
            <p className="text-xl font-semibold tracking-tight text-foreground">{c.value}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── New Culling Form ─────────────────────────────────────────────────────────

interface NewCullingFormProps {
  onClose: () => void;
  onSubmit: (r: Partial<CullingRecord>) => void;
}

function NewCullingForm({ onClose, onSubmit }: NewCullingFormProps) {
  const [form, setForm] = useState({
    farm: FARMS[0],
    house: '',
    flock: '',
    birdType: 'Broiler',
    breed: 'Cobb 500',
    quantity: '',
    reason: '' as CullingReason | '',
    reasonDetail: '',
    authorizedBy: '',
    recordedBy: '',
    notes: '',
    requireApproval: true,
    cullingDate: new Date().toISOString().split('T')[0],
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const houses = HOUSES_BY_FARM[form.farm] || [];
  const flocks = MOCK_FLOCK_SUMMARIES.filter(
    f => f.farm === form.farm && (!form.house || f.house === form.house)
  );
  const selectedFlock = MOCK_FLOCK_SUMMARIES.find(f => f.flockNumber === form.flock);

  const set = (k: string, v: string | boolean) => {
    setForm(p => ({ ...p, [k]: v }));
    setErrors(p => ({ ...p, [k]: '' }));
  };

  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.farm) e.farm = 'Farm is required';
    if (!form.flock) e.flock = 'Flock is required';
    if (!form.quantity || Number(form.quantity) <= 0) e.quantity = 'Quantity must be greater than 0';
    if (selectedFlock && Number(form.quantity) > selectedFlock.currentBirds) e.quantity = `Cannot exceed current population (${selectedFlock.currentBirds.toLocaleString()})`;
    if (!form.reason) e.reason = 'Removal reason is required';
    if (!form.authorizedBy) e.authorizedBy = 'Authorization is required';
    if (!form.recordedBy) e.recordedBy = 'Recorder is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (e: React.FormEvent, asDraft = false) => {
    e.preventDefault();
    if (!asDraft && !validate()) return;
    onSubmit({
      ...form,
      quantity: Number(form.quantity),
      status: asDraft ? 'draft' : form.requireApproval ? 'pending_approval' : 'approved',
    });
    onClose();
  };

  const popAfter = selectedFlock && form.quantity
    ? selectedFlock.currentBirds - Number(form.quantity)
    : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-red-100">
              <Scissors className="h-4 w-4 text-red-600" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-foreground">Record Bird Culling</h2>
              <p className="text-xs text-muted-foreground">Authorized removal — population OUT movement</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted transition-colors">
            <X className="h-4 w-4 text-muted-foreground" />
          </button>
        </div>

        <form onSubmit={e => handleSubmit(e, false)} className="p-5 space-y-5">
          {/* Section A — Location & Flock */}
          <div>
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3 flex items-center gap-1.5">
              <Layers className="h-3.5 w-3.5" /> Section A — Location & Flock
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-foreground mb-1">Culling Date <span className="text-red-500">*</span></label>
                <input type="date" value={form.cullingDate} onChange={e => set('cullingDate', e.target.value)}
                  className="w-full text-sm border border-border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/30" />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground mb-1">Farm <span className="text-red-500">*</span></label>
                <select value={form.farm} onChange={e => set('farm', e.target.value)}
                  className="w-full text-sm border border-border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/30 bg-white">
                  {FARMS.map(f => <option key={f}>{f}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground mb-1">Poultry House</label>
                <select value={form.house} onChange={e => set('house', e.target.value)}
                  className="w-full text-sm border border-border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/30 bg-white">
                  <option value="">All Houses</option>
                  {houses.map(h => <option key={h}>{h}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground mb-1">Flock <span className="text-red-500">*</span></label>
                <select value={form.flock} onChange={e => set('flock', e.target.value)}
                  className={`w-full text-sm border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/30 bg-white ${errors.flock ? 'border-red-400' : 'border-border'}`}>
                  <option value="">Select flock…</option>
                  {flocks.map(f => (
                    <option key={f.flockNumber} value={f.flockNumber}>
                      {f.flockNumber} — {f.flockName} ({f.currentBirds.toLocaleString()} birds)
                    </option>
                  ))}
                </select>
                {errors.flock && <p className="text-xs text-red-500 mt-1">{errors.flock}</p>}
              </div>
            </div>
          </div>

          {/* Population Preview */}
          {selectedFlock && (
            <div className="rounded-lg bg-slate-50 border border-border/60 p-3 flex items-center gap-4 text-sm">
              <div className="text-center">
                <p className="text-xs text-muted-foreground">Current Population</p>
                <p className="font-semibold text-foreground">{selectedFlock.currentBirds.toLocaleString()}</p>
              </div>
              <TrendingDown className="h-4 w-4 text-red-500 flex-shrink-0" />
              <div className="text-center">
                <p className="text-xs text-muted-foreground">Cull Quantity</p>
                <p className="font-semibold text-red-600">{form.quantity ? `−${Number(form.quantity).toLocaleString()}` : '—'}</p>
              </div>
              <div className="h-6 w-px bg-border" />
              <div className="text-center">
                <p className="text-xs text-muted-foreground">Population After</p>
                <p className={`font-semibold ${popAfter !== null && popAfter < 0 ? 'text-red-600' : 'text-foreground'}`}>
                  {popAfter !== null ? popAfter.toLocaleString() : '—'}
                </p>
              </div>
            </div>
          )}

          {/* Section B — Removal Details */}
          <div>
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3 flex items-center gap-1.5">
              <Scissors className="h-3.5 w-3.5" /> Section B — Removal Details
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-foreground mb-1">Bird Type</label>
                <select value={form.birdType} onChange={e => set('birdType', e.target.value)}
                  className="w-full text-sm border border-border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/30 bg-white">
                  {['Broiler', 'Layer', 'Breeder', 'Pullet', 'Cockerel', 'Chick', 'Local/Indigenous', 'Other'].map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground mb-1">Quantity to Cull <span className="text-red-500">*</span></label>
                <input type="number" min="1" value={form.quantity} onChange={e => set('quantity', e.target.value)} placeholder="0"
                  className={`w-full text-sm border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/30 ${errors.quantity ? 'border-red-400' : 'border-border'}`} />
                {errors.quantity && <p className="text-xs text-red-500 mt-1">{errors.quantity}</p>}
              </div>
            </div>

            {/* Removal Reason Cards */}
            <div className="mt-3">
              <label className="block text-xs font-medium text-foreground mb-2">Removal Reason <span className="text-red-500">*</span></label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {(Object.entries(REASON_CONFIG) as [CullingReason, typeof REASON_CONFIG[CullingReason]][]).map(([key, cfg]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => set('reason', key)}
                    className={`text-left p-2.5 rounded-lg border-2 transition-all ${
                      form.reason === key
                        ? 'border-current shadow-sm'
                        : 'border-border hover:border-border/80 hover:bg-muted/30'
                    }`}
                    style={form.reason === key ? { borderColor: cfg.color, backgroundColor: cfg.bg } : {}}
                  >
                    <p className="text-xs font-semibold" style={form.reason === key ? { color: cfg.color } : { color: '#374151' }}>{cfg.label}</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5 leading-tight">{cfg.description}</p>
                  </button>
                ))}
              </div>
              {errors.reason && <p className="text-xs text-red-500 mt-1">{errors.reason}</p>}
            </div>

            <div className="mt-3">
              <label className="block text-xs font-medium text-foreground mb-1">Reason Detail / Diagnosis</label>
              <input type="text" value={form.reasonDetail} onChange={e => set('reasonDetail', e.target.value)}
                placeholder="e.g. Newcastle disease — confirmed by vet on 05 Sep 2026"
                className="w-full text-sm border border-border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/30" />
            </div>
          </div>

          {/* Section C — Authorization */}
          <div>
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3 flex items-center gap-1.5">
              <Shield className="h-3.5 w-3.5" /> Section C — Authorization
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-foreground mb-1">Authorized By <span className="text-red-500">*</span></label>
                <select value={form.authorizedBy} onChange={e => set('authorizedBy', e.target.value)}
                  className={`w-full text-sm border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/30 bg-white ${errors.authorizedBy ? 'border-red-400' : 'border-border'}`}>
                  <option value="">Select authorizer…</option>
                  {APPROVERS.map(a => <option key={a}>{a}</option>)}
                </select>
                {errors.authorizedBy && <p className="text-xs text-red-500 mt-1">{errors.authorizedBy}</p>}
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground mb-1">Recorded By <span className="text-red-500">*</span></label>
                <select value={form.recordedBy} onChange={e => set('recordedBy', e.target.value)}
                  className={`w-full text-sm border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/30 bg-white ${errors.recordedBy ? 'border-red-400' : 'border-border'}`}>
                  <option value="">Select recorder…</option>
                  {RECORDERS.map(r => <option key={r}>{r}</option>)}
                </select>
                {errors.recordedBy && <p className="text-xs text-red-500 mt-1">{errors.recordedBy}</p>}
              </div>
            </div>
            <div className="mt-3">
              <label className="block text-xs font-medium text-foreground mb-1">Notes</label>
              <textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={2}
                placeholder="Additional observations, vet report reference, disposal method…"
                className="w-full text-sm border border-border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none" />
            </div>
            <div className="mt-3 flex items-center gap-2">
              <input type="checkbox" id="requireApproval" checked={form.requireApproval}
                onChange={e => set('requireApproval', e.target.checked)}
                className="h-4 w-4 rounded border-border text-primary" />
              <label htmlFor="requireApproval" className="text-xs text-foreground">Requires manager approval before posting to population ledger</label>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-2 pt-2 border-t">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground border border-border rounded-lg hover:bg-muted transition-colors">
              Cancel
            </button>
            <button type="button" onClick={e => handleSubmit(e, true)}
              className="px-4 py-2 text-sm font-medium text-foreground border border-border rounded-lg hover:bg-muted transition-colors">
              Save Draft
            </button>
            <button type="submit"
              className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors"
              style={{ backgroundColor: '#dc2626' }}>
              {form.requireApproval ? 'Submit for Approval' : 'Post Record'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Detail Drawer ────────────────────────────────────────────────────────────

interface DetailDrawerProps {
  record: CullingRecord;
  onClose: () => void;
  onApprove: (id: string) => void;
  onReject: (id: string, reason: string) => void;
  onPost: (id: string) => void;
}

function DetailDrawer({ record, onClose, onApprove, onReject, onPost }: DetailDrawerProps) {
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectInput, setShowRejectInput] = useState(false);
  const statusCfg = STATUS_CONFIG[record.status];
  const reasonCfg = REASON_CONFIG[record.reason];

  const timeline = [
    { label: 'Created', time: record.createdAt, done: true },
    { label: 'Submitted for Approval', time: record.submittedAt, done: !!record.submittedAt },
    { label: 'Approved', time: record.approvedAt, done: !!record.approvedAt },
    { label: 'Posted to Ledger', time: record.postedAt, done: !!record.postedAt },
  ];

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/30">
      <div className="bg-white w-full max-w-md h-full overflow-y-auto shadow-xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b sticky top-0 bg-white z-10">
          <div>
            <p className="text-xs text-muted-foreground font-medium">{record.cullingNumber}</p>
            <h3 className="text-sm font-semibold text-foreground">{record.flock}</h3>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted transition-colors">
            <X className="h-4 w-4 text-muted-foreground" />
          </button>
        </div>

        <div className="p-5 space-y-5 flex-1">
          {/* Status + Reason */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
              style={{ color: statusCfg.color, backgroundColor: statusCfg.bg }}>
              <statusCfg.icon className="h-3 w-3" /> {statusCfg.label}
            </span>
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
              style={{ color: reasonCfg.color, backgroundColor: reasonCfg.bg }}>
              {reasonCfg.label}
            </span>
          </div>

          {/* Population Impact */}
          {record.populationBefore !== undefined && (
            <div className="rounded-lg border border-border/60 bg-slate-50 p-3">
              <p className="text-xs font-semibold text-muted-foreground mb-2">Population Impact</p>
              <div className="flex items-center gap-3 text-sm">
                <div className="text-center">
                  <p className="text-xs text-muted-foreground">Before</p>
                  <p className="font-semibold">{record.populationBefore.toLocaleString()}</p>
                </div>
                <TrendingDown className="h-4 w-4 text-red-500" />
                <div className="text-center">
                  <p className="text-xs text-muted-foreground">Culled</p>
                  <p className="font-semibold text-red-600">−{record.quantity.toLocaleString()}</p>
                </div>
                <div className="h-6 w-px bg-border" />
                <div className="text-center">
                  <p className="text-xs text-muted-foreground">After</p>
                  <p className="font-semibold">{record.populationAfter?.toLocaleString()}</p>
                </div>
              </div>
            </div>
          )}

          {/* Details Grid */}
          <div className="space-y-2">
            {[
              { label: 'Culling Date', value: record.cullingDate },
              { label: 'Farm', value: record.farm },
              { label: 'House', value: record.house },
              { label: 'Flock Number', value: record.flockNumber },
              { label: 'Bird Type', value: record.birdType },
              { label: 'Breed', value: record.breed },
              { label: 'Quantity Culled', value: record.quantity.toLocaleString() },
              { label: 'Reason Detail', value: record.reasonDetail || '—' },
              { label: 'Authorized By', value: record.authorizedBy },
              { label: 'Recorded By', value: record.recordedBy },
              { label: 'Approved By', value: record.approvedBy || '—' },
            ].map(row => (
              <div key={row.label} className="flex items-start justify-between gap-2 text-sm">
                <span className="text-xs text-muted-foreground w-32 flex-shrink-0">{row.label}</span>
                <span className="text-xs font-medium text-foreground text-right">{row.value}</span>
              </div>
            ))}
          </div>

          {/* Notes */}
          {record.notes && (
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3">
              <p className="text-xs font-semibold text-amber-800 mb-1">Notes</p>
              <p className="text-xs text-amber-700">{record.notes}</p>
            </div>
          )}

          {/* Rejection Reason */}
          {record.status === 'rejected' && record.rejectionReason && (
            <div className="rounded-lg bg-red-50 border border-red-200 p-3">
              <p className="text-xs font-semibold text-red-800 mb-1">Rejection Reason</p>
              <p className="text-xs text-red-700">{record.rejectionReason}</p>
              {record.rejectedBy && <p className="text-xs text-red-500 mt-1">— {record.rejectedBy}</p>}
            </div>
          )}

          {/* Approval Timeline */}
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Approval Timeline</p>
            <div className="space-y-3">
              {timeline.map((step, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className={`flex h-6 w-6 items-center justify-center rounded-full flex-shrink-0 mt-0.5 ${step.done ? 'bg-green-100' : 'bg-muted'}`}>
                    {step.done
                      ? <CheckCircle className="h-3.5 w-3.5 text-green-600" />
                      : <div className="h-2 w-2 rounded-full bg-muted-foreground/40" />}
                  </div>
                  <div>
                    <p className={`text-xs font-medium ${step.done ? 'text-foreground' : 'text-muted-foreground'}`}>{step.label}</p>
                    {step.time && (
                      <p className="text-[10px] text-muted-foreground">
                        {new Date(step.time).toLocaleString('en-UG', { dateStyle: 'medium', timeStyle: 'short' })}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Action Footer */}
        {(record.status === 'pending_approval' || record.status === 'approved') && (
          <div className="border-t p-4 space-y-2 sticky bottom-0 bg-white">
            {record.status === 'pending_approval' && (
              <>
                {showRejectInput ? (
                  <div className="space-y-2">
                    <input value={rejectReason} onChange={e => setRejectReason(e.target.value)}
                      placeholder="Rejection reason (required)…"
                      className="w-full text-sm border border-border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/30" />
                    <div className="flex gap-2">
                      <button onClick={() => setShowRejectInput(false)}
                        className="flex-1 py-2 text-sm border border-border rounded-lg hover:bg-muted transition-colors">Cancel</button>
                      <button onClick={() => { if (rejectReason.trim()) { onReject(record.id, rejectReason); onClose(); } }}
                        className="flex-1 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors">Confirm Reject</button>
                    </div>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <button onClick={() => setShowRejectInput(true)}
                      className="flex-1 py-2 text-sm font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors">
                      Reject
                    </button>
                    <button onClick={() => { onApprove(record.id); onClose(); }}
                      className="flex-1 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors">
                      Approve
                    </button>
                  </div>
                )}
              </>
            )}
            {record.status === 'approved' && (
              <button onClick={() => { onPost(record.id); onClose(); }}
                className="w-full py-2 text-sm font-medium text-white rounded-lg transition-colors"
                style={{ backgroundColor: '#dc2626' }}>
                Post to Population Ledger
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function BirdCullingContent() {
  const [records, setRecords] = useState<CullingRecord[]>(MOCK_CULLING_RECORDS);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<CullingStatus | 'all'>('all');
  const [reasonFilter, setReasonFilter] = useState<CullingReason | 'all'>('all');
  const [showForm, setShowForm] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<CullingRecord | null>(null);
  const [showFilters, setShowFilters] = useState(false);

  const nextNumber = `CUL-2026-${String(records.length + 1).padStart(6, '0')}`;

  const filtered = records.filter(r => {
    const matchSearch = !search ||
      r.cullingNumber.toLowerCase().includes(search.toLowerCase()) ||
      r.flock.toLowerCase().includes(search.toLowerCase()) ||
      r.farm.toLowerCase().includes(search.toLowerCase()) ||
      r.flockNumber.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'all' || r.status === statusFilter;
    const matchReason = reasonFilter === 'all' || r.reason === reasonFilter;
    return matchSearch && matchStatus && matchReason;
  });

  const handleSubmit = (data: Partial<CullingRecord>) => {
    const selectedFlock = MOCK_FLOCK_SUMMARIES.find(f => f.flockNumber === data.flock);
    const newRecord: CullingRecord = {
      id: `cull-${Date.now()}`,
      cullingNumber: nextNumber,
      cullingDate: data.cullingDate || new Date().toISOString().split('T')[0],
      farm: data.farm || '',
      house: data.house || '',
      flock: selectedFlock ? `${selectedFlock.flockName}` : data.flock || '',
      flockNumber: data.flock || '',
      birdType: data.birdType || 'Broiler',
      breed: data.breed || '',
      quantity: data.quantity || 0,
      reason: (data.reason as CullingReason) || 'other',
      reasonDetail: data.reasonDetail,
      populationBefore: selectedFlock?.currentBirds,
      populationAfter: selectedFlock ? selectedFlock.currentBirds - (data.quantity || 0) : undefined,
      authorizedBy: data.authorizedBy || '',
      recordedBy: data.recordedBy || '',
      notes: data.notes,
      status: (data.status as CullingStatus) || 'draft',
      submittedAt: data.status === 'pending_approval' ? new Date().toISOString() : undefined,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    } as CullingRecord;
    setRecords(prev => [newRecord, ...prev]);
  };

  const handleApprove = (id: string) => {
    setRecords(prev => prev.map(r => r.id === id
      ? { ...r, status: 'approved', approvedBy: 'Farm Manager', approvedAt: new Date().toISOString() }
      : r));
  };

  const handleReject = (id: string, reason: string) => {
    setRecords(prev => prev.map(r => r.id === id
      ? { ...r, status: 'rejected', rejectedBy: 'Farm Manager', rejectionReason: reason }
      : r));
  };

  const handlePost = (id: string) => {
    setRecords(prev => prev.map(r => r.id === id
      ? { ...r, status: 'posted', postedAt: new Date().toISOString() }
      : r));
  };

  return (
    <div className="p-4 sm:p-6 space-y-5">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold text-foreground">Bird Culling</h1>
          <p className="text-sm text-muted-foreground">Authorized bird removals — population OUT movements</p>
        </div>
        <button onClick={() => setShowForm(true)}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors self-start sm:self-auto"
          style={{ backgroundColor: '#dc2626' }}>
          <Plus className="h-4 w-4" /> Record Culling
        </button>
      </div>

      {/* KPI Cards */}
      <CullingSummaryCards records={records} />

      {/* Filters Bar */}
      <div className="bg-white rounded-xl border border-border/60 shadow-sm p-3">
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search by number, flock, farm…"
              className="w-full pl-9 pr-3 py-2 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30" />
          </div>
          <button onClick={() => setShowFilters(p => !p)}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-border rounded-lg hover:bg-muted transition-colors">
            <Filter className="h-4 w-4 text-muted-foreground" />
            Filters
            <ChevronDown className={`h-3.5 w-3.5 text-muted-foreground transition-transform ${showFilters ? 'rotate-180' : ''}`} />
          </button>
        </div>
        {showFilters && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3 pt-3 border-t border-border/60">
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Status</label>
              <select value={statusFilter} onChange={e => setStatusFilter(e.target.value as CullingStatus | 'all')}
                className="w-full text-sm border border-border rounded-lg px-2 py-1.5 bg-white focus:outline-none">
                <option value="all">All Statuses</option>
                {(Object.keys(STATUS_CONFIG) as CullingStatus[]).map(s => (
                  <option key={s} value={s}>{STATUS_CONFIG[s].label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Removal Reason</label>
              <select value={reasonFilter} onChange={e => setReasonFilter(e.target.value as CullingReason | 'all')}
                className="w-full text-sm border border-border rounded-lg px-2 py-1.5 bg-white focus:outline-none">
                <option value="all">All Reasons</option>
                {(Object.keys(REASON_CONFIG) as CullingReason[]).map(r => (
                  <option key={r} value={r}>{REASON_CONFIG[r].label}</option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-border/60 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border/60 bg-muted/30">
                {['Culling #', 'Date', 'Farm / House', 'Flock', 'Qty', 'Reason', 'Authorized By', 'Status', 'Actions'].map(h => (
                  <th key={h} className="text-left text-xs font-semibold text-muted-foreground px-4 py-3 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border/40">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={9} className="text-center py-10 text-sm text-muted-foreground">
                    No culling records found
                  </td>
                </tr>
              ) : filtered.map(record => {
                const statusCfg = STATUS_CONFIG[record.status];
                const reasonCfg = REASON_CONFIG[record.reason];
                return (
                  <tr key={record.id} className="hover:bg-muted/20 transition-colors">
                    <td className="px-4 py-3">
                      <span className="font-mono text-xs font-medium text-foreground">{record.cullingNumber}</span>
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">{record.cullingDate}</td>
                    <td className="px-4 py-3">
                      <p className="text-xs font-medium text-foreground">{record.farm}</p>
                      <p className="text-[10px] text-muted-foreground">{record.house}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-xs font-medium text-foreground">{record.flockNumber}</p>
                      <p className="text-[10px] text-muted-foreground truncate max-w-[120px]">{record.birdType} · {record.breed}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm font-semibold text-red-600">−{record.quantity.toLocaleString()}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium"
                        style={{ color: reasonCfg.color, backgroundColor: reasonCfg.bg }}>
                        {reasonCfg.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <User className="h-3 w-3 text-muted-foreground" />
                        <span className="text-xs text-foreground">{record.authorizedBy}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium"
                        style={{ color: statusCfg.color, backgroundColor: statusCfg.bg }}>
                        <statusCfg.icon className="h-3 w-3" />
                        {statusCfg.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button onClick={() => setSelectedRecord(record)}
                          className="p-1.5 rounded-lg hover:bg-muted transition-colors" title="View details">
                          <Eye className="h-3.5 w-3.5 text-muted-foreground" />
                        </button>
                        {record.status === 'pending_approval' && (
                          <>
                            <button onClick={() => handleApprove(record.id)}
                              className="p-1.5 rounded-lg hover:bg-green-50 transition-colors" title="Approve">
                              <CheckCircle className="h-3.5 w-3.5 text-green-600" />
                            </button>
                            <button onClick={() => handleReject(record.id, 'Rejected from table')}
                              className="p-1.5 rounded-lg hover:bg-red-50 transition-colors" title="Reject">
                              <XCircle className="h-3.5 w-3.5 text-red-500" />
                            </button>
                          </>
                        )}
                        {record.status === 'approved' && (
                          <button onClick={() => handlePost(record.id)}
                            className="p-1.5 rounded-lg hover:bg-red-50 transition-colors" title="Post to ledger">
                            <Activity className="h-3.5 w-3.5 text-red-600" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Table Footer */}
        <div className="px-4 py-3 border-t border-border/60 flex items-center justify-between">
          <p className="text-xs text-muted-foreground">
            Showing {filtered.length} of {records.length} records
          </p>
          <div className="flex items-center gap-1">
            {(Object.keys(STATUS_CONFIG) as CullingStatus[]).map(s => {
              const count = records.filter(r => r.status === s).length;
              if (!count) return null;
              const cfg = STATUS_CONFIG[s];
              return (
                <span key={s} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium"
                  style={{ color: cfg.color, backgroundColor: cfg.bg }}>
                  {count} {cfg.label}
                </span>
              );
            })}
          </div>
        </div>
      </div>

      {/* Modals */}
      {showForm && <NewCullingForm onClose={() => setShowForm(false)} onSubmit={handleSubmit} />}
      {selectedRecord && (
        <DetailDrawer
          record={selectedRecord}
          onClose={() => setSelectedRecord(null)}
          onApprove={handleApprove}
          onReject={handleReject}
          onPost={handlePost}
        />
      )}
    </div>
  );
}
